import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, Key } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import { ApplicationType, FileType, FormDataType, FormElementType, FormType, OptionType, ReportType, SectionType } from '../helpers/types';
import { fileIcon } from '../helpers/utils';
import Chip from './Chip';
import _ from 'lodash';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

type Props = {
    formData: FormDataType;
    application: ApplicationType | ReportType;
    showEditButton?: boolean;
    type?: 'application' | 'report';
    applicationId?: number;
};

const Accordion = (props: Props) => {
    const { formData, application, showEditButton = false, type, applicationId } = props;
    const { t } = useTranslation();

    const Item = ({ section, firstElement }: { section: SectionType; firstElement: boolean }) => (
        <div className="accordion-item">
            <h2 className="accordion-header">
                <button className={`accordion-button ${!firstElement ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target={`#collapse${section.url}`} aria-expanded="true" aria-controls={`collapse${section.url}`}>
                    {section.title}
                </button>
            </h2>
            <div id={`collapse${section.url}`} className={`accordion-collapse collapse ${firstElement ? 'show' : ''}`} data-bs-parent="#summaryAccordion">
                <div className="accordion-body">
                    {section.forms.map((form, index) => (
                        <Fragment key={index}>
                            <FormGroup form={form} section={section} application={application} formIndex={index} />
                            {index < section.forms.length - 1 && <hr />}
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );

    const FormGroup = ({ form, section, application, formIndex }: { form: FormType; section: SectionType; application: ApplicationType | ReportType; formIndex: number }) =>
        form.elements
            // `textblock` er innhold uten verdi — den hører ikke hjemme i en
            // oppsummering av det søker har fylt ut.
            ?.filter((element) => element.type !== 'hidden' && element.type !== 'textblock')
            .map((element, index) => {
                if (element.type === 'checkbox' && element.hide_if && formData[element.hide_if.name] === element.hide_if.value) {
                    return null;
                }
                return (
                    <Fragment key={index}>
                        <div className="form-group">
                            <div className="d-flex justify-content-between">
                                {element.label === 'Utlegg' ? <h6>Oversikt over utlegg</h6> : <h6>{element.label}</h6> }
                                {index === 0 && showEditButton ? (
                                    <Link to={`/${type === 'application' ? 'soknadskjema' : 'rapport'}${applicationId ? `/${applicationId}` : ''}/${application.url}/${section.url}/${form.url}`} className="no-print text-decoration-none unbreakable">
                                        {formIndex === 0 ? t('edit') : null}
                                        <span className="border border-1 border-primary rounded-circle d-inline-flex align-items-center justify-content-center ms-1" style={{ width: '17px', height: '17px' }}>
                                            <FontAwesomeIcon icon={['fal', 'pencil']} className="ms-0" style={{ fontSize: '9px' }} />
                                        </span>
                                    </Link>
                                ) : null}
                            </div>

                            <FormElement element={element} />
                        </div>
                    </Fragment>
                );
            });

    const FormElement = ({ element }: { element: FormElementType }) => {
        const { t } = useTranslation();

        if(!formData[element.name]) {
            return <span className="text-muted">Ikke fylt ut</span>;
        }

        switch (element.type) {
            case 'text':
            case 'textarea':
                return <p>{formData[element.name] ?? ''}</p>;
            case 'numberformat':
                return (
                    <p>
                        <NumericFormat value={formData[element.name]} displayType="text" thousandSeparator=" " />
                    </p>
                );
            case 'brreg_required':
                return (
                    <p>
                        <PatternFormat value={formData[element.name]} format="### ### ###" displayType="text" />
                    </p>
                );
            case 'dropdown':
                return <p>{element.options.find((option) => option.value === formData[element.name])?.label}</p>;
            case 'RadioGroup':
            case 'bigradio':
                return <p>{element.alternatives.find((alternative) => alternative.value === formData[element.name])?.label}</p>;
            case 'dropdowncheck':
                let array = [];

                if (!Array.isArray(formData[element.name])) {
                    array = JSON.parse(formData[element.name]);
                } else {
                    array = formData[element.name];
                }

                let options = [];
                if (element.options) {
                    if(!Array.isArray(element.options)) {
                        options = JSON.parse(element.options);
                    } else {
                        options = element.options;
                    }
                }

                return (
                    <div className="mb-3">
                        {array?.map((item: OptionType, index: Key) => (
                            <Chip key={index} label={options.find((option: OptionType) => option.value === item)?.label || item.label} />
                        ))}
                    </div>
                );
            case 'radiogroupdepending':
                return (
                    <Fragment>
                        <p>{element.alternatives.find((alternative) => alternative.value === formData[element.name])?.label}</p>

                        {element.depends.map((depend) => {
                            if (formData[element.name] === depend.radiovalue) {
                                return depend.innerElements.map((element, index) => (
                                    <Fragment key={index}>
                                        <p className="fw-medium mb-1">{element.label}</p>
                                        <FormElement element={element} />
                                    </Fragment>
                                ));
                            }
                        })}
                    </Fragment>
                );
            case 'inputmask':
                return (
                    <div className="mb-3">
                        <PatternFormat value={formData[element.name]} format={element.mask} patternChar="0" displayType="text" />
                    </div>
                );
            case 'account':
                return (
                    <div className="mb-3">
                        <PatternFormat value={formData[element.name]} format={element.mask} patternChar="0" displayType="text" />
                    </div>
                );
            case 'fileuploadsingle':
                const file = formData[element.name];

                return (
                    <div className="mb-3">
                        {file ? (
                            Array.isArray(file) ? (
                                file.map((file: FileType, index: number) => (
                                    <div className="row" key={index}>
                                        {file.download_url ? (
                                            <a className="col text-primary text-decoration-none mt-3 mb-2 my-lg-0" href={import.meta.env.VITE_API_BASE_URL + file.download_url}>
                                                <FontAwesomeIcon icon={fileIcon(file.name.split('.').pop() ?? '')} className="me-2" />
                                                <span>{file.name}</span>
                                            </a>
                                        ) : (
                                            <p className="col text-primary text-decoration-none mt-3 mb-2 my-lg-0">
                                                <FontAwesomeIcon icon={fileIcon(file.name?.split('.').pop() ?? '')} className="me-2" />
                                                <span>{file.name}</span>
                                            </p>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="row">
                                    {file.download_url ? (
                                        <a className="col text-primary text-decoration-none mt-3 mb-2 my-lg-0" href={import.meta.env.VITE_API_BASE_URL + file.download_url}>
                                            <FontAwesomeIcon icon={fileIcon(file.name.split('.').pop() ?? '')} className="me-2" />
                                            <span>{file.name}</span>
                                        </a>
                                    ) : (
                                        <p className="col text-primary text-decoration-none mt-3 mb-2 my-lg-0">
                                            <FontAwesomeIcon icon={fileIcon(file.name?.split('.').pop() ?? '')} className="me-2" />
                                            <span>{file.name}</span>
                                        </p>
                                    )}
                                </div>
                            )
                        ) : (
                            <span>Ingen fil</span>
                        )}
                    </div>
                );
            case 'fileupload':
                const files = formData[element.name];
                
                return (
                    <div className="mb-3">
                        {files && Array.isArray(files) && files.length ? (
                            files?.map((file: FileType, index: number) => (
                                <div className="row" key={index}>
                                    <a className="col text-primary text-decoration-none mt-3 mb-2 my-lg-0" href={import.meta.env.VITE_API_BASE_URL + file.download_url}>
                                        <FontAwesomeIcon icon={fileIcon(file.name.split('.').pop() ?? '')} className="me-2" />
                                        <span>{file.name}</span>
                                    </a>
                                </div>
                            ))
                        ) : (
                            <span>Ingen filer</span>
                        )}
                    </div>
                );
            case 'ToggleSwitch':
            case 'checkbox':
                return formData[element.name] ? t('yes') : t('no');
            case 'date':
                return (
                    <div className="mb-3">
                        <p>{formData[element.name] ? format(formData[element.name], 'dd. MMMM yyyy', { locale: nb }) : 'Ingen dato'}</p>
                    </div>
                );
            case 'expensetable': {
                const rows: any[] = (() => {
                    const v = formData[element.name];
                    if (Array.isArray(v)) return v;
                    if (typeof v === 'string' && v) { try { return JSON.parse(v); } catch { return []; } }
                    return [];
                })();

                const total = rows.reduce((sum: number, row: any) => sum + (Number(String(row.belop ?? '').replace(/\s+/g, '')) || 0), 0);
                    return (
                        <div className="mb-2">
                            <div className="d-flex gap-3 mb-1 small fw-semibold text-muted border-bottom pb-1">
                                <span className="flex-grow-1">Beskrivelse</span>
                                <span style={{ minWidth: 100 }}>Beløp (kr)</span>
                                <span style={{ minWidth: 140 }}>Bilag</span>
                            </div>
                            {rows.map((row: any, i: number) => (
                                <div key={i} className="d-flex gap-3 mb-1 small border-bottom py-1">
                                    <span className="flex-grow-1">{row.beskrivelse}</span>
                                    <span style={{ minWidth: 100 }}>
                                        <NumericFormat value={row.belop} displayType="text" thousandSeparator=" " />
                                    </span>
                                <span className="text-muted" style={{ minWidth: 140 }}>
                                    {row.bilag ? (() => {
                    const name = row.bilag.name;
                    const url = !(row.bilag instanceof File) ? row.bilag.download_url : null;
                    return url ? (
                        <a href={import.meta.env.VITE_API_BASE_URL + url} target="_blank" rel="noreferrer" className="text-decoration-none">
                            <FontAwesomeIcon icon={fileIcon(name.split('.').pop() ?? '')} className="me-1" />
                            <span className="text-primary">{name}</span>
                        </a>
                    ) : (
                        <><FontAwesomeIcon icon={fileIcon(name.split('.').pop() ?? '')} className="me-1" /><span className="text-primary">{name}</span></>
                    );
                })() : '—'}
                </span>
                </div>
            ))}
            <div className="text-end fw-bold mt-2">
                Totalsum: Kr. <NumericFormat value={total} displayType="text" thousandSeparator=" " />
            </div>
        </div>
    );
                                    }
            case 'budgetlines': {
                const budgetRows: Array<Record<string, any>> = (() => {
                    const v = formData[element.name];
                    if (Array.isArray(v)) return v;
                    if (typeof v === 'string' && v) { try { return JSON.parse(v); } catch { return []; } }
                    return [];
                })();
                const budgetCols = (element as any).columns ?? [];
                const total = budgetRows.reduce((sum: number, row: any) =>
                    sum + (Number(String(row.belop ?? '').replace(/\s+/g, '')) || 0), 0);
                if (budgetRows.length === 0) return <span className="text-muted small">Ingen poster</span>;
                return (
                    <div className="mb-3">
                        <table className="table table-sm table-borderless mb-1">
                            <thead>
                                <tr>
                                    {budgetCols.map((col: any) => <th key={col.name} className="text-muted small fw-semibold">{col.label}</th>)}
                                    <th className="text-end text-muted small fw-semibold">Beløp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {budgetRows.map((row: any, i: number) => (
                                    <tr key={i}>
                                        {budgetCols.map((col: any) => <td key={col.name}>{row[col.name] ?? ''}</td>)}
                                        <td className="text-end">{(Number(String(row.belop ?? '').replace(/\s+/g, '')) || 0).toLocaleString('nb-NO')} kr</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="text-end small fw-bold">Totalt: {total.toLocaleString('nb-NO')} kr</div>
                    </div>
                );
            }
            case 'repeater': {
                const repeaterRows: Array<Record<string, any>> = (() => {
                    const v = formData[element.name];
                    if (Array.isArray(v)) return v;
                    if (typeof v === 'string' && v) { try { return JSON.parse(v); } catch { return []; } }
                    return [];
                })();
                const repeaterCols = (element as any).columns ?? [];
                if (repeaterRows.length === 0) return <span className="text-muted small">Ingen poster</span>;
                return (
                    <div className="mb-3">
                        <table className="table table-sm table-borderless mb-1">
                            <thead>
                                <tr>
                                    {repeaterCols.map((col: any) => <th key={col.name} className="text-muted small fw-semibold">{col.label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {repeaterRows.map((row: any, i: number) => (
                                    <tr key={i}>
                                        {repeaterCols.map((col: any) => <td key={col.name}>{row[col.name] ?? ''}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }
        }

        return (
            <>
                {JSON.stringify(formData[element.name])}
            </>
        );
    };

    return (
        <div className="accordion application-info-accordeon" id="summaryAccordion">
            {application?.sections
                ?.filter((section: SectionType) => section.type === 'form' || section.type === 'user_info' || section.type === 'pre')
                .map((section: SectionType, index: Key) => (
                    <Item key={index + section.url} firstElement={index === 0} section={section} />
                ))}
        </div>
    );
};

export default Accordion;
